import { useState, useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useBox } from "@react-three/cannon";
import { usePlayerContext } from "../../lib/context/PlayerContext";
import { MEZZANINE_Y, ROOF_HEIGHT } from "../../lib/consts";

// ─── Dimensions ───────────────────────────────────────────────────────────────
const W = 2.0; // X span: width = 2
const D = 2.0; // Z span: depth = 2
const DOOR_H = 3.2; // visible door height per floor
const SHAFT_H = ROOF_HEIGHT; // full shaft height
const DOOR_SLIDE = 0.9; // how far each panel slides when open
const DOOR_PANEL_W = D / 2; // each door panel = 1.0

const doorFaceX = W / 2; // 1.0 local

interface ElevatorProps {
  position?: [number, number, number];
}

// ─── Helper function to create text texture ────────────────────────────────
function createTextTexture(text: string, color = "white"): THREE.Texture {
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  
  // Background
  ctx.fillStyle = "#333333";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  
  // Text
  ctx.fillStyle = color;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "bold 50px Arial";
  ctx.fillText(text, 64, 64);
  
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

// ─── Button with text ─────────────────────────────────────────────────────────
function ElevatorButton({
  position,
  text,
  color,
  onClick,
  emissiveColor,
}: {
  position: [number, number, number];
  text: string;
  color: string;
  emissiveColor: string;
  onClick: (e: any) => void;
}) {
  const textureRef = useRef<THREE.Texture | null>(null);
  
  const texture = useMemo(() => {
    if (textureRef.current) textureRef.current.dispose();
    textureRef.current = createTextTexture(text, "white");
    return textureRef.current;
  }, [text]);

  return (
    <group position={position}>
      {/* Button base */}
      <mesh
        onClick={onClick}
        onPointerOver={(e) => {
          e.stopPropagation();
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={(e) => {
          e.stopPropagation();
          document.body.style.cursor = "auto";
        }}
      >
        <boxGeometry args={[0.22, 0.22, 0.06]} />
        <meshStandardMaterial
          color={color}
          emissive={emissiveColor}
          emissiveIntensity={0.4}
          roughness={0.3}
          metalness={0.5}
        />
      </mesh>
      {/* Text on button - positioned above button surface */}
      {texture && (
        <mesh position={[0, 0, 0.05]}>
          <planeGeometry args={[0.2, 0.2]} />
          <meshBasicMaterial
            map={texture}
            transparent={true}
            depthWrite={false}
          />
        </mesh>
      )}
    </group>
  );
}
function DoorPair({
  baseY,
  open,
}: {
  baseY: number;
  open: boolean;
}) {
  const leftRef = useRef<THREE.Mesh>(null);
  const rightRef = useRef<THREE.Mesh>(null);

  const closedLeft = -DOOR_PANEL_W / 2;
  const closedRight = DOOR_PANEL_W / 2;

  useFrame((_, delta) => {
    if (!leftRef.current || !rightRef.current) return;
    const speed = 3.5;
    const tl = open ? closedLeft - DOOR_SLIDE : closedLeft;
    const tr = open ? closedRight + DOOR_SLIDE : closedRight;
    leftRef.current.position.z = THREE.MathUtils.lerp(
      leftRef.current.position.z,
      tl,
      speed * delta
    );
    rightRef.current.position.z = THREE.MathUtils.lerp(
      rightRef.current.position.z,
      tr,
      speed * delta
    );
  });

  const centerY = baseY + DOOR_H / 2;

  return (
    <>
      {/* Left panel */}
      <mesh ref={leftRef} position={[doorFaceX, centerY, closedLeft]} castShadow>
        <boxGeometry args={[0.08, DOOR_H, DOOR_PANEL_W]} />
        <meshStandardMaterial
          color="#9eaab5"
          metalness={0.85}
          roughness={0.1}
        />
      </mesh>
      {/* Right panel */}
      <mesh ref={rightRef} position={[doorFaceX, centerY, closedRight]} castShadow>
        <boxGeometry args={[0.08, DOOR_H, DOOR_PANEL_W]} />
        <meshStandardMaterial
          color="#9eaab5"
          metalness={0.85}
          roughness={0.1}
        />
      </mesh>
      {/* Door frame – top bar */}
      <mesh position={[doorFaceX - 0.04, baseY + DOOR_H + 0.12, 0]}>
        <boxGeometry args={[0.15, 0.22, D * 0.85]} />
        <meshStandardMaterial color="#555" metalness={0.6} />
      </mesh>
    </>
  );
}

  // ─── Main Elevator Component ──────────────────────────────────────────────────
export default function Elevator({ position = [3, 0, -13] }: ElevatorProps) {
  const { teleportPlayer, getPlayerPosition } = usePlayerContext();
  
  // World coordinates
  const elevatorX = position[0];
  const elevatorY = position[1];
  const elevatorZ = position[2];

  const [currentFloor, setCurrentFloor] = useState<1 | 2>(1);
  const [doorOpen, setDoorOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [manualOpenRequested, setManualOpenRequested] = useState(false);
  const lastCollisionTimeRef = useRef(0);

  // Elevator bounds (world coordinates)
  const elevatorBounds = {
    minX: elevatorX - W / 2,
    maxX: elevatorX + W / 2,
    minZ: elevatorZ - D / 2,
    maxZ: elevatorZ + D / 2,
  };

  const isPlayerInElevator = () => {
    const playerPos = getPlayerPosition();
    if (!playerPos) return false;
    return (
      playerPos[0] >= elevatorBounds.minX &&
      playerPos[0] <= elevatorBounds.maxX &&
      playerPos[2] >= elevatorBounds.minZ &&
      playerPos[2] <= elevatorBounds.maxZ
    );
  };

  // Check if player is near elevator within 2 units
  const isPlayerNearElevator = () => {
    const playerPos = getPlayerPosition();
    if (!playerPos) return false;
    
    const dx = playerPos[0] - elevatorX;
    const dz = playerPos[2] - elevatorZ;
    const distance = Math.sqrt(dx * dx + dz * dz);
    
    return distance <= 5.0;
  };

  // Play collision sound effect
  const playCollisionSound = () => {
    const now = Date.now();
    // Prevent sound spam - only play once per 300ms
    if (now - lastCollisionTimeRef.current < 300) return;
    lastCollisionTimeRef.current = now;

    // Create audio context
    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Collision sound: quick beep
    oscillator.frequency.value = 200;
    oscillator.type = "sine";

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContext.currentTime + 0.1
    );

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  };

  // Auto-open/close door based on player proximity
  useFrame(() => {
    const playerNear = isPlayerNearElevator();
    const playerInside = isPlayerInElevator();
    
    // If player is near but not inside, open door
    if (playerNear && !playerInside && !busy) {
      setDoorOpen(true);
    }
    
    // If player is not near and not inside, close door
    if (!playerNear && !playerInside && doorOpen) {
      setDoorOpen(false);
    }
  });

  const f1Open = doorOpen && currentFloor === 1;
  const f2Open = doorOpen && currentFloor === 2;

  // ── Physics colliders for elevator walls ──────────────────────────────────
  // Back wall (west side - solid box) - limited size
  const [backWallRef] = useBox(() => ({
    type: "Static" as const,
    position: [elevatorX - W / 2 - 0.04, elevatorY + SHAFT_H / 2, elevatorZ],
    args: [0.08, SHAFT_H, D],
    onCollide: () => playCollisionSound(),
  }));

  // South wall (south side - solid box) - limited size
  const [southWallRef] = useBox(() => ({
    type: "Static" as const,
    position: [elevatorX, elevatorY + SHAFT_H / 2, elevatorZ - D / 2 - 0.04],
    args: [W, SHAFT_H, 0.08],
    onCollide: () => playCollisionSound(),
  }));

  // North wall (north side - solid box) - limited size
  const [northWallRef] = useBox(() => ({
    type: "Static" as const,
    position: [elevatorX, elevatorY + SHAFT_H / 2, elevatorZ + D / 2 + 0.04],
    args: [W, SHAFT_H, 0.08],
    onCollide: () => playCollisionSound(),
  }));

  return (
    <group position={position}>
      {/* Physics bodies - render as invisible meshes */}
      <mesh ref={backWallRef} visible={false} />
      <mesh ref={southWallRef} visible={false} />
      <mesh ref={northWallRef} visible={false} />

      {/* ── SHAFT SHELL ──────────────────────────────────────────────── */}
      {/* Floor slab */}
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <boxGeometry args={[W, 0.1, D]} />
        <meshStandardMaterial color="#333" roughness={0.9} />
      </mesh>

      {/* Ceiling — offset 0.25 below roof to avoid Z-fighting */}
      <mesh position={[0, SHAFT_H - 0.25, 0]}>
        <boxGeometry args={[W, 0.1, D]} />
        <meshStandardMaterial color="#444" />
      </mesh>

      {/* Ceiling decorative panel - inside elevator visible */}
      <mesh position={[0, SHAFT_H - 0.5, 0]} castShadow receiveShadow>
        <boxGeometry args={[W - 0.15, 0.05, D - 0.15]} />
        <meshStandardMaterial 
          color="#1a1a1a" 
          metalness={0.4}
          roughness={0.3}
        />
      </mesh>

      {/* Ceiling light panel - bright white */}
      <mesh position={[0, SHAFT_H - 0.55, 0]} castShadow receiveShadow>
        <boxGeometry args={[W - 0.3, 0.02, D - 0.3]} />
        <meshStandardMaterial 
          color="#f0f0f0"
          metalness={0.5}
          roughness={0.2}
          emissive="#ffffff"
          emissiveIntensity={0.3}
        />
      </mesh>

      {/* Back wall (phía trong phòng, hướng tây) - positioned just inside physics collider */}
      <mesh position={[-W / 2 - 0.11, SHAFT_H / 2, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.08, SHAFT_H, D + 0.3]} />
        <meshStandardMaterial
          color="#d0d0d0"
          metalness={0.6}
          roughness={0.2}
        />
      </mesh>

      {/* South side wall - positioned just inside physics collider */}
      <mesh position={[0, SHAFT_H / 2, -D / 2 - 0.11]} castShadow receiveShadow>
        <boxGeometry args={[W + 0.3, SHAFT_H, 0.08]} />
        <meshStandardMaterial color="#c8c8c8" roughness={0.5} />
      </mesh>

      {/* North side wall - positioned just inside physics collider */}
      <mesh position={[0, SHAFT_H / 2, D / 2 + 0.11]} castShadow receiveShadow>
        <boxGeometry args={[W + 0.3, SHAFT_H, 0.08]} />
        <meshStandardMaterial color="#c8c8c8" roughness={0.5} />
      </mesh>

      {/* Corner guards - South-West corner */}
      <mesh position={[-W / 2 - 0.11, SHAFT_H / 2, -D / 2 - 0.11]} castShadow receiveShadow>
        <boxGeometry args={[0.12, SHAFT_H, 0.12]} />
        <meshStandardMaterial color="#b8b8b8" roughness={0.5} />
      </mesh>

      {/* Corner guards - South-East corner */}
      <mesh position={[W / 2 + 0.11, SHAFT_H / 2, -D / 2 - 0.11]} castShadow receiveShadow>
        <boxGeometry args={[0.12, SHAFT_H, 0.12]} />
        <meshStandardMaterial color="#b8b8b8" roughness={0.5} />
      </mesh>

      {/* Corner guards - North-West corner */}
      <mesh position={[-W / 2 - 0.11, SHAFT_H / 2, D / 2 + 0.11]} castShadow receiveShadow>
        <boxGeometry args={[0.12, SHAFT_H, 0.12]} />
        <meshStandardMaterial color="#b8b8b8" roughness={0.5} />
      </mesh>

      {/* Corner guards - North-East corner */}
      <mesh position={[W / 2 + 0.11, SHAFT_H / 2, D / 2 + 0.11]} castShadow receiveShadow>
        <boxGeometry args={[0.12, SHAFT_H, 0.12]} />
        <meshStandardMaterial color="#b8b8b8" roughness={0.5} />
      </mesh>

      {/* ── FLOOR 1 DOORS ────────────────────────────────────────────── */}
      <DoorPair baseY={0} open={f1Open} />

      {/* FLOOR 1 - Ceiling inside cabin */}
      <mesh position={[0, DOOR_H + 0.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[W - 0.1, 0.1, D - 0.1]} />
        <meshStandardMaterial 
          color="#2a2a2a" 
          metalness={0.5}
          roughness={0.3}
        />
      </mesh>

      {/* ── FLOOR 2 DOORS ────────────────────────────────────────────── */}
      <DoorPair baseY={MEZZANINE_Y} open={f2Open} />

      {/* FLOOR 2 - Ceiling inside cabin */}
      <mesh position={[0, MEZZANINE_Y + DOOR_H + 0.1, 0]} castShadow receiveShadow>
        <boxGeometry args={[W - 0.1, 0.1, D - 0.1]} />
        <meshStandardMaterial 
          color="#2a2a2a" 
          metalness={0.5}
          roughness={0.3}
        />
      </mesh>

      {/* ══════════════════════════════════════════════════════════════════
          CALL BUTTON on WALL (bên ngoài thang máy)
          ════════════════════════════════════════════════════════════════ */}

      {/* ── FLOOR 1: Nút gọi thang máy ── */}
      {/* Call button - nút "2" để mở cửa thang máy tầng 2 */}
      <ElevatorButton
        position={[doorFaceX + 0.16, 1.5, -D / 2 - 0.15]}
        text="2"
        color={doorOpen && currentFloor === 2 ? "#28a745" : "#666"}
        emissiveColor={doorOpen && currentFloor === 2 ? "#28a745" : "#111"}
        onClick={(e) => {
          e.stopPropagation();
          if (!busy && currentFloor === 2) {
            // Nếu đã ở tầng 2, toggle mở/đóng cửa
            setManualOpenRequested(!doorOpen);
            setDoorOpen(!doorOpen);
          } else if (!busy && currentFloor !== 2) {
            // Nếu không ở tầng 2, chỉ mở cửa (không teleport)
            setManualOpenRequested(true);
            setDoorOpen(true);
          }
        }}
      />

      {/* Close button - Floor 1 */}
      <ElevatorButton
        position={[doorFaceX - 1, 1.5, -D / 2 + 0.1]}
        text="▶◀"
        color={doorOpen ? "#dc3545" : "#666"}
        emissiveColor={doorOpen ? "#dc3545" : "#111"}
        onClick={(e) => {
          e.stopPropagation();
          if (doorOpen && !busy) {
            setDoorOpen(false);
          }
        }}
      />

      {/* Open button - Floor 1 */}
      <ElevatorButton
        position={[doorFaceX - 0.745, 1.5, -D / 2 + 0.1]}
        text="◀▶"
        color={!doorOpen ? "#28a745" : "#666"}
        emissiveColor={!doorOpen ? "#28a745" : "#111"}
        onClick={(e) => {
          e.stopPropagation();
          if (!doorOpen && !busy) {
            setDoorOpen(true);
          }
        }}
      />

      {/* Floor destination button - shows floor 2 when on floor 1 */}
      <ElevatorButton
        position={[doorFaceX - 0.5, 1.5, -D / 2 + 0.1]}
        text={currentFloor === 1 ? "2" : "1"}
        color="#ffc107"
        emissiveColor="#ffc107"
        onClick={(e) => {
          e.stopPropagation();
          const targetFloor = currentFloor === 1 ? 2 : 1;
          if (!busy && currentFloor !== targetFloor) {
            setBusy(true);
            setDoorOpen(false);
            setTimeout(() => {
              const cabX = position[0];
              const cabZ = position[2];
              const cabY = targetFloor === 1 ? 1.1 : MEZZANINE_Y + 1.1;
              teleportPlayer(cabX, cabY, cabZ);
              setCurrentFloor(targetFloor);
              setTimeout(() => {
                setDoorOpen(true);
                const checkPlayerExit = setInterval(() => {
                  if (!isPlayerInElevator()) {
                    setDoorOpen(false);
                    setBusy(false);
                    clearInterval(checkPlayerExit);
                  }
                }, 100);
                setTimeout(() => {
                  setDoorOpen(false);
                  setBusy(false);
                  clearInterval(checkPlayerExit);
                }, 30000);
              }, 500);
            }, 800);
          }
        }}
      />

      {/* Close button - Floor 2 */}
      <ElevatorButton
        position={[doorFaceX - 1, MEZZANINE_Y + 1.5, -D / 2 + 0.1]}
        text="▶◀"
        color={doorOpen ? "#dc3545" : "#666"}
        emissiveColor={doorOpen ? "#dc3545" : "#111"}
        onClick={(e) => {
          e.stopPropagation();
          if (doorOpen && !busy) {
            setDoorOpen(false);
          }
        }}
      />

      {/* Open button - Floor 2 */}
      <ElevatorButton
        position={[doorFaceX - 0.745, MEZZANINE_Y + 1.5, -D / 2 + 0.1]}
        text="◀▶"
        color={!doorOpen ? "#28a745" : "#666"}
        emissiveColor={!doorOpen ? "#28a745" : "#111"}
        onClick={(e) => {
          e.stopPropagation();
          if (!doorOpen && !busy) {
            setDoorOpen(true);
          }
        }}
      />

      {/* Floor destination button - shows floor 1 when on floor 2 */}
      <ElevatorButton
        position={[doorFaceX - 0.5, MEZZANINE_Y + 1.5, -D / 2 + 0.1]}
        text={currentFloor === 1 ? "2" : "1"}
        color="#ffc107"
        emissiveColor="#ffc107"
        onClick={(e) => {
          e.stopPropagation();
          const targetFloor = currentFloor === 1 ? 2 : 1;
          if (!busy && currentFloor !== targetFloor) {
            setBusy(true);
            setDoorOpen(false);
            setTimeout(() => {
              const cabX = position[0];
              const cabZ = position[2];
              const cabY = targetFloor === 1 ? 1.1 : MEZZANINE_Y + 1.1;
              teleportPlayer(cabX, cabY, cabZ);
              setCurrentFloor(targetFloor);
              setTimeout(() => {
                setDoorOpen(true);
                const checkPlayerExit = setInterval(() => {
                  if (!isPlayerInElevator()) {
                    setDoorOpen(false);
                    setBusy(false);
                    clearInterval(checkPlayerExit);
                  }
                }, 100);
                setTimeout(() => {
                  setDoorOpen(false);
                  setBusy(false);
                  clearInterval(checkPlayerExit);
                }, 30000);
              }, 500);
            }, 800);
          }
        }}
      />

      {/* ── FLOOR 2: Nút gọi thang máy ── */}
      {/* Call button - nút "1" để mở cửa thang máy tầng 1 */}
      <ElevatorButton
        position={[doorFaceX + 0.16, MEZZANINE_Y + 1.5, -D / 2 - 0.15]}
        text="1"
        color={doorOpen && currentFloor === 1 ? "#28a745" : "#666"}
        emissiveColor={doorOpen && currentFloor === 1 ? "#28a745" : "#111"}
        onClick={(e) => {
          e.stopPropagation();
          if (!busy && currentFloor === 1) {
            // Nếu đã ở tầng 1, toggle mở/đóng cửa
            setManualOpenRequested(!doorOpen);
            setDoorOpen(!doorOpen);
          } else if (!busy && currentFloor !== 1) {
            // Nếu không ở tầng 1, chỉ mở cửa (không teleport)
            setManualOpenRequested(true);
            setDoorOpen(true);
          }
        }}
      />

      {/* ── INTERIOR LIGHTS ──────────────────────────────────────────── */}
      <pointLight
        position={[0, DOOR_H - 0.3, 0]}
        intensity={2.0}
        distance={4}
        color="#fff8e7"
      />
      <pointLight
        position={[0, MEZZANINE_Y + DOOR_H - 0.3, 0]}
        intensity={2.0}
        distance={4}
        color="#fff8e7"
      />
    </group>
  );
}
